import React, { useEffect, useMemo, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { authHeaders } from "../utils/authHeaders";
import "./UserPopup.css";

const API = process.env.REACT_APP_API_URL || "/api";

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

const toDateInputValue = (v) => {
  if (!v) return "";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  if (typeof v === "string" && v.includes("T")) return v.split("T")[0];
  return String(v);
};

const CORE_FIELDS = [
  "id",
  "full_name",
  "name",
  "username",
  "email",
  "phone",
  "whatsapp",
  "gender",
  "birth_date",
  "full_address",
  "address",
  "desa",
  "village",
  "kecamatan",
  "sub_district",
  "kabupaten",
  "district",
  "provinsi",
  "province",
  "jumlah_kebun",
  "farm_count",
  "luas_kebun",
  "farm_size",
  "role",
  "institution",
  "profile_pic",
  "facebook_url",
  "instagram_url",
  "youtube_url",
  "tiktok_url",
  "photo_url",
  "avatar_url",
  "latitude",
  "longitude",
];

export default function UserPopup({ user, mode, onClose, onSave }) {
  const [formData, setFormData] = useState(user || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFormData(user || {});
  }, [user]);

  const isEdit = mode === "edit";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    const v = value === "" ? "" : Number(value);
    setFormData((f) => ({ ...f, [name]: v }));
  };

  const extraFields = useMemo(() => {
    const obj = formData || {};
    const keys = Object.keys(obj);
    const ignored = new Set([
      ...CORE_FIELDS,
      "password",
      "created_at",
      "updated_at",
      "__v",
    ]);
    return keys.filter((k) => !ignored.has(k)).sort();
  }, [formData]);

  const submitUpdate = async (payload) => {
    const id = payload?.id || formData?.id;
    if (!id) throw new Error("ID user tidak ditemukan");

    const endpoints = [`${API}/user/${id}`, `${API}/users/${id}`];
    let lastErrText = "";

    for (const url of endpoints) {
      const res = await fetch(url, {
        method: "PUT",
        headers: authHeaders(getStoredUser(), { "Content-Type": "application/json" }),
        body: JSON.stringify(payload),
      });

      if (res.status === 404) continue;

      // kalau gagal, ambil text/json agar jelas error-nya
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        let msg = t || `HTTP ${res.status}`;
        try {
          const parsed = t ? JSON.parse(t) : null;
          if (parsed?.detail) msg = `${parsed.message || "Gagal memperbarui user"} (${parsed.detail})`;
          else if (parsed?.message) msg = parsed.message;
        } catch {}
        lastErrText = msg;
        throw new Error(msg);
      }

      // sukses: parse json
      const json = await res.json().catch(() => ({}));
      const updated =
        json?.user || json?.data || json?.result || json?.users || json;

      // kalau backend hanya balas {success:true}, fallback pakai payload
      if (updated && typeof updated === "object" && !Array.isArray(updated)) {
        return updated;
      }
      return payload;
    }

    throw new Error(lastErrText || "Endpoint update tidak ditemukan");
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);

      const { password, ...rest } = formData;

      const birth = toDateInputValue(rest.birth_date);
      const payload = {
        ...rest,
        birth_date: birth || null,
      };

      // penting: pastikan ID ikut terkirim
      if (!payload.id) payload.id = formData?.id;

      const updated = await submitUpdate(payload);

      // update state list di parent
      onSave(updated);

      alert("Data user berhasil diperbarui.");
    } catch (e) {
      console.error("Update user error:", e);
      alert(`Gagal menyimpan perubahan.\n${e?.message || ""}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup-box">
        <div className="popup-header">
          <h3>{isEdit ? "Edit Profil User" : "Lihat Profil User"}</h3>
          <button className="popup-close-btn" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        <div className="popup-form">
          <div className="form-group full-width">
            <label>Foto Profil (URL)</label>
            <input
              name="profile_pic"
              value={formData.profile_pic || formData.photo_url || formData.avatar_url || ""}
              onChange={(e) => setFormData((f) => ({ ...f, profile_pic: e.target.value }))}
              disabled={!isEdit}
              placeholder="https://....jpg/png"
            />
          </div>

          <div className="form-group">
            <label>Nama</label>
            <input
              name="full_name"
              value={formData.full_name || formData.name || ""}
              onChange={(e) => setFormData((f) => ({ ...f, full_name: e.target.value }))}
              disabled={!isEdit}
            />
          </div>

          <div className="form-group">
            <label>Username</label>
            <input
              name="username"
              value={formData.username || ""}
              onChange={handleChange}
              disabled={!isEdit}
            />
          </div>

          <div className="form-group">
            <label>Email</label>
            <input name="email" value={formData.email || ""} onChange={handleChange} disabled={!isEdit} />
          </div>

          <div className="form-group">
            <label>No HP</label>
            <input name="phone" value={formData.phone || ""} onChange={handleChange} disabled={!isEdit} />
          </div>

          <div className="form-group">
            <label>No WhatsApp</label>
            <input
              name="whatsapp"
              value={formData.whatsapp || ""}
              onChange={handleChange}
              disabled={!isEdit}
              placeholder="62xxxxxxxxxx"
            />
          </div>

          <div className="form-group">
            <label>Jenis Kelamin</label>
            <select name="gender" value={formData.gender || ""} onChange={handleChange} disabled={!isEdit}>
              <option value="">Pilih</option>
              <option value="Laki-laki">Laki-laki</option>
              <option value="Perempuan">Perempuan</option>
            </select>
          </div>

          <div className="form-group">
            <label>Tanggal Lahir</label>
            <input
              type="date"
              name="birth_date"
              value={toDateInputValue(formData.birth_date)}
              onChange={handleChange}
              disabled={!isEdit}
            />
          </div>

          <div className="form-group full-width">
            <label>Alamat</label>
            <input
              name="full_address"
              value={formData.full_address || formData.address || ""}
              onChange={(e) => setFormData((f) => ({ ...f, full_address: e.target.value }))}
              disabled={!isEdit}
            />
          </div>

          <div className="form-group">
            <label>Desa</label>
            <input
              name="desa"
              value={formData.desa || formData.village || ""}
              onChange={(e) => setFormData((f) => ({ ...f, desa: e.target.value }))}
              disabled={!isEdit}
            />
          </div>

          <div className="form-group">
            <label>Kecamatan</label>
            <input
              name="kecamatan"
              value={formData.kecamatan || formData.sub_district || ""}
              onChange={(e) => setFormData((f) => ({ ...f, kecamatan: e.target.value }))}
              disabled={!isEdit}
            />
          </div>

          <div className="form-group">
            <label>Kabupaten</label>
            <input
              name="kabupaten"
              value={formData.kabupaten || formData.district || ""}
              onChange={(e) => setFormData((f) => ({ ...f, kabupaten: e.target.value }))}
              disabled={!isEdit}
            />
          </div>

          <div className="form-group">
            <label>Provinsi</label>
            <input
              name="provinsi"
              value={formData.provinsi || formData.province || ""}
              onChange={(e) => setFormData((f) => ({ ...f, provinsi: e.target.value }))}
              disabled={!isEdit}
            />
          </div>

          <div className="form-group">
            <label>Jumlah Kebun</label>
            <input
              type="number"
              name="jumlah_kebun"
              value={formData.jumlah_kebun ?? ""}
              onChange={handleNumberChange}
              disabled={!isEdit}
            />
          </div>

          <div className="form-group">
            <label>Luas Kebun (ha)</label>
            <input
              type="number"
              step="0.1"
              name="luas_kebun"
              value={formData.luas_kebun ?? ""}
              onChange={handleNumberChange}
              disabled={!isEdit}
            />
          </div>

          <div className="form-group">
            <label>Institusi</label>
            <input
              name="institution"
              value={formData.institution || ""}
              onChange={handleChange}
              disabled={!isEdit}
            />
          </div>

          <div className="form-group">
            <label>Role</label>
            {isEdit ? (
              <select name="role" value={formData.role || "user"} onChange={handleChange}>
                <option value="user">User</option>
                <option value="mitra sahabat kakao">Mitra Sahabat Kakao</option>
                <option value="sahabat kakao">Sahabat Kakao</option>
                <option value="field team">Field Team</option>
                <option value="management">Management</option>
                <option value="admin">Admin</option>
              </select>
            ) : (
              <input name="role" value={formData.role || ""} disabled />
            )}
          </div>

          <div className="form-group">
            <label>Latitude</label>
            <input
              type="number"
              step="0.000001"
              name="latitude"
              value={formData.latitude ?? ""}
              onChange={handleNumberChange}
              disabled={!isEdit}
            />
          </div>

          <div className="form-group">
            <label>Longitude</label>
            <input
              type="number"
              step="0.000001"
              name="longitude"
              value={formData.longitude ?? ""}
              onChange={handleNumberChange}
              disabled={!isEdit}
            />
          </div>

          <div className="form-group full-width">
            <label>Facebook (Opsional)</label>
            <input
              type="url"
              name="facebook_url"
              value={formData.facebook_url || ""}
              onChange={handleChange}
              disabled={!isEdit}
              placeholder="https://facebook.com/username"
            />
          </div>

          <div className="form-group">
            <label>Instagram (Opsional)</label>
            <input
              type="url"
              name="instagram_url"
              value={formData.instagram_url || ""}
              onChange={handleChange}
              disabled={!isEdit}
              placeholder="https://instagram.com/username"
            />
          </div>

          <div className="form-group">
            <label>YouTube (Opsional)</label>
            <input
              type="url"
              name="youtube_url"
              value={formData.youtube_url || ""}
              onChange={handleChange}
              disabled={!isEdit}
              placeholder="https://youtube.com/@channel"
            />
          </div>

          <div className="form-group">
            <label>TikTok (Opsional)</label>
            <input
              type="url"
              name="tiktok_url"
              value={formData.tiktok_url || ""}
              onChange={handleChange}
              disabled={!isEdit}
              placeholder="https://tiktok.com/@username"
            />
          </div>

          {extraFields.length > 0 && (
            <div className="form-group full-width" style={{ marginTop: 6 }}>
              <label style={{ fontWeight: 700 }}>Data Lainnya (Opsional)</label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                  marginTop: "8px",
                }}
              >
                {extraFields.map((key) => (
                  <div className="form-group" key={key} style={{ margin: 0 }}>
                    <label style={{ fontSize: 12, opacity: 0.8 }}>{key}</label>
                    <input
                      name={key}
                      value={
                        formData[key] === null || formData[key] === undefined
                          ? ""
                          : String(formData[key])
                      }
                      onChange={handleChange}
                      disabled={!isEdit}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="popup-buttons">
          <button className="btn-close" onClick={onClose} disabled={saving}>
            Tutup
          </button>
          {isEdit && (
            <button className="btn-save" onClick={handleSubmit} disabled={saving}>
              {saving ? "Menyimpan..." : "Simpan"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}



